import {MemoryCache} from './memory-state.ts';
import type {Memory} from './memory.ts';

export type MemoryNode = {
	name: string;
	missing: boolean;
	links: string[];
	backlinks: string[];
}

export function extractLinks(content: string): string[] {
	if (!content) return [];
	const matches = content.matchAll(/\[\[([^\]|]+)(?:\|[^\]]*)?\]\]/g);
	return [...new Set([...matches].map(m => m[1].trim()))];
}

export function patchGraph(mems: Memory[], nodes: MemoryNode[], changed: Memory[]): MemoryNode[] {
	const nameSet = new Set(mems.map(m => m.name));
	const byName = new Map(nodes.map(n => [n.name, n]));

	const ensureNode = (name: string): MemoryNode => {
		let n = byName.get(name);
		if (!n) {
			n = {name, missing: !nameSet.has(name), links: [], backlinks: []};
			byName.set(name, n);
		}
		return n;
	};

	for (const m of changed) {
		const node = ensureNode(m.name);
		node.missing = false; // real memory, promotes any pre-existing ghost entry
		const oldLinks = m.links ?? [];
		const newLinks = extractLinks(m.content).filter(l => l !== m.name);

		for (const target of oldLinks.filter(l => !newLinks.includes(l))) {
			const t = byName.get(target);
			if (!t) continue;
			t.backlinks = t.backlinks.filter(n => n !== m.name);
			if (t.missing && !t.backlinks.length) byName.delete(target); // fully dereferenced ghost
		}
		for (const target of newLinks.filter(l => !oldLinks.includes(l))) {
			const t = ensureNode(target);
			if (!t.backlinks.includes(m.name)) t.backlinks.push(m.name);
		}

		m.links = newLinks;
		node.links = newLinks;
	}

	for (const m of mems) {
		const n = byName.get(m.name);
		if (n) m.backlinks = n.backlinks;
	}

	return [...byName.values()];
}

export function rebuildGraph(memories: Memory[] | MemoryCache): MemoryNode[] {
	const mems = memories instanceof MemoryCache ? memories.memories : memories;
	const nameSet = new Set(mems.map(m => m.name));

	for (const m of mems) m.links = extractLinks(m.content).filter(l => l !== m.name);
	for (const m of mems) m.backlinks = [];
	for (const m of mems) {
		for (const link of m.links) {
			const target = mems.find(t => t.name === link);
			if (target) target.backlinks.push(m.name);
		}
	}

	const nodes: MemoryNode[] = mems.map(m => ({
		name: m.name,
		missing: false,
		links: m.links,
		backlinks: m.backlinks,
	}));

	const ghosts = new Set<string>();
	for (const node of nodes) {
		for (const link of node.links) {
			if (!nameSet.has(link)) ghosts.add(link);
		}
	}

	return [
		...nodes,
		...[...ghosts].map(name => ({
			name,
			missing: true,
			links: [],
			backlinks: nodes.filter(n => n.links.includes(name)).map(n => n.name),
		})),
	];
}

export function renderMemoryGraph(nodes: MemoryNode[]): string {
	if (!nodes.length) return 'No memories yet.';

	const groups = new Map<string, (MemoryNode & {label: string})[]>();
	for (const node of nodes) {
		const [prefix, ...rest] = node.name.split('/');
		const group = rest.length ? prefix : 'Root';
		const label = rest.length ? rest.join('/') : node.name;
		if (!groups.has(group)) groups.set(group, []);
		groups.get(group)!.push({...node, label});
	}

	const ghostCount = nodes.filter(n => n.missing).length;
	const lines = [`Memory Graph (${nodes.length} nodes, ${ghostCount} ghost${ghostCount === 1 ? '' : 's'})`, ''];

	for (const group of [...groups.keys()].sort()) {
		const items = groups.get(group)!.sort((a, b) => a.label.localeCompare(b.label));
		lines.push(`${group}/`);
		items.forEach((n, i) => {
			const last = i === items.length - 1;
			const branch = last ? '└─' : '├─';
			const pad = last ? '  ' : '│ ';
			const tag = n.missing ? ' (ghost)' : '';
			lines.push(`  ${branch} ${n.label}${tag}`);
			if (n.links.length) lines.push(`  ${pad}   → ${n.links.join(', ')}`);
			if (n.backlinks.length) lines.push(`  ${pad}   ← ${n.backlinks.join(', ')}`);
		});
		lines.push('');
	}

	return lines.join('\n').trimEnd();
}
